/* eslint-disable max-lines */

import React, { useContext } from 'react'

import { Col, Row } from 'antd'
import _ from 'lodash'
import PropTypes from 'prop-types'

import { useGetIncomeByStreams } from 'renter/hooks/api'
import ApplicationDataTag from 'shared/components/ApplicationDataTag'
import Icon from 'shared/components/Icon'
import Spacer from 'shared/components/Spacer'
import Tile from 'shared/components/Tile'
import { MediumTitle, RegularText } from 'shared/components/Typography'
import Well from 'shared/components/Well'
import { EMAILS_WITH_INCOME_GRAPH_VIEW_PERMISSION } from "shared/config/authConstants";
import {
  MAX_INCOMES_NUMBER_IN_STREAM,
  VERIFICATION_STATUSES,
} from 'shared/config/constants'

import { usePermissions } from "shared/hooks";
import { useActiveUser, useGetRoles } from 'shared/hooks/api'
import { getSkippedIncomeAssetsInfo } from 'shared/utils/application'
import { isManagerApp } from "shared/utils/auth";
import { printDollarsFromCents } from 'shared/utils/dollar-print'
import { getCurrentEmployments } from 'shared/utils/employmentAndIncome'
import { incomeAndAssetPropType } from 'shared/utils/propTypes'

import ApplicationContext from '../applicationContext'
import { BankAccounts } from '../components/AssetsSection/BankAccounts'
import { CashAvailable } from '../components/AssetsSection/CashAvailable'
import { IncomeBucketChart } from '../components/IncomesBucketsChart/IncomeBucketChart'
import { UnverifiedPotentialBalances } from '../components/AssetsSection/UnverifiedPotentialBalances'

import { ExcludedIncomeSection } from '../components/IncomeStreamsSection/ExcludedIncomeSection'
import { IncludedIncomeSection } from '../components/IncomeStreamsSection/IncludedIncomeSection'
import { IncomeStreams } from '../components/IncomeStreamsSection/IncomeStreams'
import { IAndBSkeleton } from '../components/Skeleton/Skeleton'
import StatedEmployers from '../components/StatedEmployers'

import { EmploymentHistory, useEmploymentHistory } from '../EmploymentHistory'

import {
  EmploymentHistoryCustom,
  ReasonWrapper,
  TileCustom,
  TitleCustom,
  IncomeNote,
  FraudBanner,
  RiskBanner,
  BannerContentContainer,
} from './styled'

const IncomeAndAssets = ({ summary, applicationSummary, ocrResults, canViewOcrResults }) => {
  const { activeUser } = useActiveUser()
  const { data: userRoles } = useGetRoles()
  const { application } = useContext(ApplicationContext)
  const safeApplication = application || {}
  const {
    dealDecisionIsMade,
    incomeGroupRule,
    isVeroGoActive,
    location,
    originalSubmitDate,
    personVrn,
    applicantVrn,
    snapshot,
  } = safeApplication
  const isVeroGoEnabled = Boolean(
    isVeroGoActive ??
      safeApplication?.veroGoActive ??
      applicationSummary?.veroGoActive ??
      safeApplication?.propertyVeroGoActive ??
      safeApplication?.property?.veroGoActive
  )
  const ocrCashBalance =
    safeApplication?.ocrCashBalance ??
    safeApplication?.ocr_cash_balance ??
    snapshot?.ocrCashBalance ??
    snapshot?.ocr_cash_balance
  const unverifiedPotentialBalanceAccounts = ocrCashBalance?.accounts ?? []
  const showUnverifiedPotentialBalancesSection =
    isManagerApp() &&
    isVeroGoEnabled &&
    !_.isEmpty(unverifiedPotentialBalanceAccounts)
  const areIncomeAndAssetChecksEnabled =
    applicationSummary?.areIncomeAndAssetChecksEnabled ??
    safeApplication?.areIncomeAndAssetChecksEnabled ??
    true
  const areIncomeStreamsVisible =
    areIncomeAndAssetChecksEnabled || isVeroGoEnabled
  const allowedProviders = isVeroGoEnabled ? 'document,vero' : undefined
  const internationalIncome =
    safeApplication?.internationalIncomePolicy ?? undefined;
  const shouldFetchIncomeByStreams =
    areIncomeStreamsVisible &&
    !_.isEmpty(safeApplication) &&
    !_.isEmpty(personVrn) &&
    !dealDecisionIsMade
  const {
    excludedStream,
    includedStream,
    isLoading: isIncomeByStreamsLoading,
  } = useGetIncomeByStreams(
    {
      allowedProviders,
      internationalIncome,
      detailed: true,
      incomeNumberLimit: MAX_INCOMES_NUMBER_IN_STREAM,
      location,
      person: personVrn,
      applicant: applicantVrn,
      groupingRule: incomeGroupRule,
      referenceDate: originalSubmitDate,
    },
    shouldFetchIncomeByStreams
  )
  const { companyPermissions } = usePermissions();
  const { canEditOcrResults } = companyPermissions || {};
  const snapshotIncomeStreams = areIncomeStreamsVisible
    ? snapshot?.incomeStreams
    : []
  const includedIncomeStream = areIncomeStreamsVisible
    ? dealDecisionIsMade
      ? snapshotIncomeStreams?.flatMap(({ streams }) =>
          streams.filter(({ isIncluded }) => isIncluded),
        ) ?? []
      : includedStream
    : []
  const excludedIncomeStream = areIncomeStreamsVisible
    ? dealDecisionIsMade
      ? snapshotIncomeStreams?.flatMap(({ streams }) =>
          streams.filter(({ isIncluded }) => !isIncluded),
        ) ?? []
      : excludedStream
    : []
  const snapshotEmployments = _.get(safeApplication, 'snapshot.employments')
  const verifiedIncome = _.get(safeApplication, 'verifiedIncome')
  const currentEmployments = getCurrentEmployments(snapshotEmployments)

  const statedEmployers = _.chain(currentEmployments).map('employer').value()

  const accounts = summary?.accounts
  const isAssetsEdited =
    summary?.assetsVerificationStatus === VERIFICATION_STATUSES.EDITED
  const isIncomeEdited =
    summary?.incomeVerificationStatus === VERIFICATION_STATUSES.EDITED

  const editedIncome =
    isManagerApp() && isIncomeEdited ? summary?.editedIncome : undefined
  const editedAssets =
    isManagerApp() && isAssetsEdited ? summary?.editedAssets : undefined

  const skippedIncomeAssets = getSkippedIncomeAssetsInfo(
    summary?.skippedReason,
    summary?.bankName,
  )

  const showFraudBanner = isManagerApp() && canViewOcrResults && excludedIncomeStream.some((stream) => stream.isFraudulent);
  const showRiskBanner = isManagerApp() && excludedIncomeStream.length > 0;

  const showReasonsToSkipApplicationBlock =
    applicationSummary?.skippedIncome &&
    applicationSummary?.skippedAsset &&
    applicationSummary?.reasonsSkipBlockDealSubmitIfIncomeDataIsMissing
      ?.length > 0

  const employmentProps = useEmploymentHistory(applicationSummary)

  const showIncomeByBucketChart =
    areIncomeAndAssetChecksEnabled &&
    activeUser?.email?.endsWith('@sayvero.com') &&
    EMAILS_WITH_INCOME_GRAPH_VIEW_PERMISSION.includes(activeUser?.email)

  return (
    <>
      {showFraudBanner && (
        <>
          <FraudBanner
            Icon={Icon.ErrorTrianglewhiteOutlineIcon}
            message={
              <BannerContentContainer>
                <RegularText>
                  Some of the applicant's income has been excluded due to potential fraud risk.
                </RegularText>
                <RegularText extraStrong>
                  <a href="#unverified-income">VIEW FRAUD SIGNALS</a>
                </RegularText>
              </BannerContentContainer>
            }
          />
          <Spacer size={showRiskBanner ? Spacer.SIZES.xs : Spacer.SIZES.sm } />
        </>
      )}
      {showRiskBanner && (
        <>
          <RiskBanner
            Icon={Icon.BlackExclamationHollow}
            message={
              <BannerContentContainer>
                <RegularText>
                  Some of the applicant’s income has been excluded due to validation checks. Please review the details to ensure accurate assessment.
                </RegularText>
                <RegularText extraStrong>
                  <a href="#unverified-income">VIEW EXCLUDED INCOME</a>
                </RegularText>
              </BannerContentContainer>
            }
          />
          <Spacer size={Spacer.SIZES.sm} />
        </>
      )}
      {showIncomeByBucketChart && (
        <TileCustom rounded>
          <Tile.Inner>
            <IncomeBucketChart />
            <IncomeNote>
              <Icon.InfoBlueIcon width={16} height={16} />
              <div>
                The graph only illustrates actual income per month, omitting any
                anticipated income.
              </div>
            </IncomeNote>
          </Tile.Inner>
        </TileCustom>
      )}
      {showReasonsToSkipApplicationBlock && (
        <Row gutter={14} type="flex">
          <Col span={24}>
            <Well lightWarning largePadding>
              <RegularText>
                Reasons applicant provided to unblock the application
                submission:
              </RegularText>
              <Spacer size={Spacer.SIZES.xs} />
              {applicationSummary?.reasonsSkipBlockDealSubmitIfIncomeDataIsMissing?.map(
                (reason) => (
                  <ReasonWrapper key={reason.key} alignItems="center">
                    <Icon.OrangeExclamationSmall className="warning-icon" />
                    <RegularText>{reason.label}</RegularText>
                  </ReasonWrapper>
                ),
              )}
            </Well>
            <Spacer size={Spacer.SIZES.sm} />
          </Col>
        </Row>
      )}
      {safeApplication?.partialUploadReason && (
        <Row gutter={14} type="flex">
          <Col span={24}>
            <Well lightWarning largePadding>
              <RegularText>
                Reason applicant provided for not uploading all required
                paystubs:
              </RegularText>
              <Spacer size={Spacer.SIZES.xs} />
              <ReasonWrapper alignItems="center">
                <Icon.OrangeExclamationSmall className="warning-icon" />
                <RegularText>{safeApplication.partialUploadReason}</RegularText>
              </ReasonWrapper>
            </Well>
            <Spacer size={Spacer.SIZES.sm} />
          </Col>
        </Row>
      )}
      {isIncomeByStreamsLoading ? (
        <IAndBSkeleton title={{ rows: 3, width: [141, 181, 69] }} />
      ) : (
        <IncludedIncomeSection streams={includedIncomeStream} canViewOcrResults={canViewOcrResults} canEditOcrResults={canEditOcrResults} editedIncome={editedIncome} />
      )}
      <CashAvailable editedAssets={editedAssets} />
      {!isIncomeByStreamsLoading && (
        <ExcludedIncomeSection streams={excludedIncomeStream} ocrResults={ocrResults} canViewOcrResults={canViewOcrResults} canEditOcrResults={canEditOcrResults} />
      )}
      {showUnverifiedPotentialBalancesSection && (
        <UnverifiedPotentialBalances
          accounts={unverifiedPotentialBalanceAccounts}
          ocrResults={ocrResults}
          canViewOcrResults={canViewOcrResults}
        />
      )}
    </>
  )
}

IncomeAndAssets.propTypes = {
  summary: incomeAndAssetPropType,
  applicationSummary: PropTypes.shape({
    areIncomeAndAssetChecksEnabled: PropTypes.bool,
    skippedIncome: PropTypes.bool,
    skippedAsset: PropTypes.bool,
    reasonsSkipBlockDealSubmitIfIncomeDataIsMissing: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
  }),
}

IncomeAndAssets.defaultProps = {
  summary: undefined,
  applicationSummary: undefined,
}

export default IncomeAndAssets
